// Specific modules rather than the public API, so the plain-logic test runner can load this file.
import {
  ReportingComparisonScopes,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import type { ReportFilterOption } from '../../../utils/report-filter-options.utils';

export const PRODUCTION_TRACKING_TITLE = 'Production Tracking';
export const PRODUCTION_TRACKING_FILTERED_SUBJECT = 'Based on Filtered Results';

const optionName = (option: ReportFilterOption): string => option.name ?? option.label;

/**
 * Who the page is about, as the title names it (TASKID-3477):
 * - one filter option picked: that RMD or MGA;
 * - several but not all: "Based on Filtered Results";
 * - nothing picked, or everything: the scope's default. Alliance Group company-wide; on an
 *   RMD's page the one MGA they have, else "RMD View"; on an MGA's page that MGA.
 */
export function getProductionTrackingSubject(args: {
  scope: ReportingComparisonScope | null;
  selectedValues: readonly string[];
  options: readonly ReportFilterOption[];
  /** The viewed agent's own MGA, for the MGA page. */
  mgaName: string | null;
}): string | null {
  const selectedValues = [...new Set(args.selectedValues)];
  const isEverythingSelected =
    args.options.length > 0 && args.options.every(option => selectedValues.includes(option.value));

  if (selectedValues.length && !isEverythingSelected) {
    const selectedOption =
      selectedValues.length === 1 ? args.options.find(option => option.value === selectedValues[0]) : null;

    return selectedOption ? optionName(selectedOption) : PRODUCTION_TRACKING_FILTERED_SUBJECT;
  }

  switch (args.scope) {
    case ReportingComparisonScopes.allianceGroup:
      return 'Alliance Group';
    case ReportingComparisonScopes.rmd: {
      const mgaOptions = args.options.filter(option => option.type === 'MGA');

      return mgaOptions.length === 1 ? optionName(mgaOptions[0]) : 'RMD View';
    }
    case ReportingComparisonScopes.mga:
      return args.mgaName?.trim() || null;
    default:
      return null;
  }
}

export function buildProductionTrackingTitle(subject: string | null): string {
  return subject ? `${PRODUCTION_TRACKING_TITLE} - ${subject}` : PRODUCTION_TRACKING_TITLE;
}

/** "As of Sep 18, 2026" from a plain ISO date; null when there is no date to name. */
export function buildProductionTrackingAsOfLabel(isoDate: string | null): string | null {
  const [year, month, day] = (isoDate ?? '').split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  const date = new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `As of ${date}`;
}
